import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Archive, Search, Loader2 } from "lucide-react";

export const ArchiveTab = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (params = {}) => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/archive", { params });
      setRows(data);
    } catch { setRows([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { search(); }, [search]);

  const run = (e) => { e?.preventDefault(); search({ q, date_from: from, date_to: to }); };

  return (
    <div data-testid="archive-tab">
      <div className="flex items-center gap-2">
        <Archive className="h-5 w-5 text-kilted-green" />
        <h2 className="font-heading text-xl font-bold">Delivery archive</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Completed jobs move here at 4:00am each day. Search by customer, con note or delivery date for dispute reference.</p>
      <form onSubmit={run} className="mt-4 flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <label className="text-xs font-semibold text-muted-foreground">Customer / recipient / con note</label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Fiona or KC-1234ABCD" data-testid="archive-search-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Delivered from</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="archive-date-from" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">to</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="archive-date-to" />
        </div>
        <Button type="submit" disabled={busy} data-testid="archive-search-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
        </Button>
      </form>
      {rows === null ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="archive-empty">No archived deliveries match.</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader><TableRow><TableHead>Con note</TableHead><TableHead>Customer</TableHead><TableHead>Recipient</TableHead><TableHead>Route</TableHead><TableHead>Delivered</TableHead><TableHead>Driver</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/orders/${r.id}`)} data-testid={`archive-row-${r.tracking_number}`}>
                  <TableCell className="font-heading font-bold">{r.tracking_number}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>
                  <TableCell>{r.recipient_name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.pickup_city} → {r.dropoff_city}</TableCell>
                  <TableCell className="text-xs">{r.delivered_at ? new Date(r.delivered_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—"}</TableCell>
                  <TableCell>{r.driver_name || "—"}</TableCell>
                  <TableCell className="text-right">${r.total?.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ArchiveTab;
